import { useMemo } from 'react';
import type { DeviceBucket } from '../../lib/api/analytics';

/**
 * Three side-by-side breakdowns of unique devices: by **OS**, by **OS
 * version**, and by **device model**. All sourced from
 * `app_session_events.{os, os_version, device_model}` (stamped by the
 * mobile clients on session_start).
 *
 * Why this view: it tells you (a) where to focus QA effort (which OS
 * version covers ~80% of users — Pareto), (b) what minimum versions to
 * support without leaving big audiences behind, and (c) which device
 * models to keep on your physical test rack vs. simulate. For a
 * Nigeria-first app the long tail of mid-range Android devices is
 * usually the surprise — this surfaces it.
 */
interface DeviceBreakdownProps {
  byOs: DeviceBucket[];
  byOsVersion: DeviceBucket[];
  byDeviceModel: DeviceBucket[];
  totalDevices: number;
  devicesMissingDetails: number;
}

export function DeviceBreakdown({
  byOs,
  byOsVersion,
  byDeviceModel,
  totalDevices,
  devicesMissingDetails,
}: DeviceBreakdownProps): JSX.Element {
  const totalOs = useMemo(
    () => byOs.reduce((sum, b) => sum + b.uniques, 0),
    [byOs],
  );
  // Reported = total minus missing — the denominator the version + model
  // breakdowns are computed against. Using totalOs here would inflate
  // smaller buckets' share% because the missing devices ARE in byOs (via
  // the platform fallback) but NOT in byOsVersion / byDeviceModel.
  const reported = Math.max(1, totalDevices - devicesMissingDetails);

  return (
    <div className="space-y-4">
      <div className="grid gap-6 lg:grid-cols-3">
        <Section
          title="Operating system"
          hint="Which platforms your audience actually uses."
          rows={byOs.map((b) => ({
            group: b.group,
            uniques: b.uniques,
            share: totalOs > 0 ? (b.uniques / totalOs) * 100 : 0,
          }))}
          accent="brand"
        />
        <Section
          title="OS version"
          hint="Where the bulk of devices are — pick your minimum support floor."
          rows={byOsVersion.map((b) => ({
            group: b.group,
            uniques: b.uniques,
            share: (b.uniques / reported) * 100,
          }))}
          accent="info"
          limit={10}
        />
        <Section
          title="Top device models"
          hint="Hardware to prioritise on your test rack."
          rows={byDeviceModel.map((b) => ({
            group: b.group,
            uniques: b.uniques,
            share: (b.uniques / reported) * 100,
          }))}
          accent="warn"
          limit={10}
        />
      </div>
      {devicesMissingDetails > 0 ? (
        <p className="border-t border-line-hair pt-3 text-[11.5px] leading-snug text-ink-subtle">
          <span className="font-mono font-semibold text-ink-muted">
            {devicesMissingDetails.toLocaleString()}
          </span>{' '}
          of {totalDevices.toLocaleString()} devices ({((devicesMissingDetails / totalDevices) * 100).toFixed(0)}%) came from app
          builds that don't yet report OS version or device model — these only
          appear in the OS column. Will phase out as users update to the latest
          build.
        </p>
      ) : null}
    </div>
  );
}

interface Row {
  group: string;
  uniques: number;
  share: number;
}

function Section({
  title,
  hint,
  rows,
  accent,
  limit,
}: {
  title: string;
  hint: string;
  rows: Row[];
  accent: 'brand' | 'info' | 'warn';
  limit?: number;
}): JSX.Element {
  const visible = limit ? rows.slice(0, limit) : rows;
  const accentColor =
    accent === 'brand'
      ? 'bg-brand'
      : accent === 'info'
        ? 'bg-sky-500'
        : 'bg-amber-500';

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <h4 className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ink-subtle">
          {title}
        </h4>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
          {rows.length} {rows.length === 1 ? 'group' : 'groups'}
        </span>
      </div>
      <p className="mb-3 text-[11.5px] leading-snug text-ink-subtle">{hint}</p>

      {visible.length === 0 ? (
        <p className="rounded-md border border-dashed border-line py-3 text-center text-xs text-ink-subtle">
          No data yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {visible.map((r) => (
            <li key={r.group} className="grid grid-cols-[1fr_auto] items-center gap-x-3">
              <div className="min-w-0">
                <div className="truncate text-[12.5px] text-ink" title={r.group}>
                  {r.group}
                </div>
                <div className="relative mt-0.5 h-1.5 overflow-hidden rounded-full bg-line-hair">
                  <span
                    className={`absolute inset-y-0 left-0 ${accentColor}`}
                    style={{ width: `${Math.max(2, Math.min(100, r.share))}%` }}
                  />
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[12.5px] font-semibold tabular-nums text-ink">
                  {r.uniques.toLocaleString()}
                </div>
                <div className="font-mono text-[10px] tabular-nums text-ink-subtle">
                  {r.share < 0.1 ? '<0.1%' : `${r.share.toFixed(1)}%`}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
